import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Paper, Typography, Card, CardContent, CardHeader, Avatar, Chip, LinearProgress, IconButton, Tooltip } from '@mui/material';
import {
  People as PeopleIcon,
  LocalHospital as DoctorIcon,
  CalendarMonth as AppointmentIcon,
  Description as ReportIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  MoreVert as MoreIcon,
  Notifications as NotificationIcon,
  Assessment as StatsIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchAdminDashboard } from '../../store/slices/dashboardSlice';

/**
 * Admin Dashboard Page
 * Premium dashboard for Civilhealth Coordinators and System Admins.
 *
 * @author Civilhealth Development Team
 */
const AdminDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { adminDashboard, loading } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    dispatch(fetchAdminDashboard());
  }, [dispatch]);

  if (loading || !adminDashboard) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  const { stats, recentAppointments, recentActivities, doctorSummary, officerSummary } = adminDashboard;

  // Stats cards data - NO BACKGROUND, just icon color
  const statsCards = [
    { title: 'Total Appointments', value: stats.totalAppointments, icon: <AppointmentIcon />, color: '#1D4ED8' },
    { title: 'Pending Appointments', value: stats.pendingAppointments, icon: <ScheduleIcon />, color: '#B45309' },
    { title: 'Total Doctors', value: stats.totalDoctors, icon: <DoctorIcon />, color: '#15803D' },
    { title: 'Total Officers', value: stats.totalOfficers, icon: <PeopleIcon />, color: '#DC2626' },
  ];

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#202124' }}>
            Admin Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome to Civilhealth Healthcare Response Management System
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statsCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {card.title}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {(card.value ?? 0).toLocaleString()}
                      </Typography>
                    </Box>
                    {/* NO BACKGROUND - Icon only */}
                    <Avatar sx={{ backgroundColor: 'transparent', color: card.color }}>
                      {card.icon}
                    </Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Phase-2 Stats (additive) */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { title: 'Total Admissions', value: adminDashboard.phaseTwo?.totalAdmissions, sub: `${adminDashboard.phaseTwo?.activeAdmissions ?? 0} active`, icon: <DoctorIcon />, color: '#F59E0B' },
            { title: 'Total Investigations', value: adminDashboard.phaseTwo?.totalInvestigations, sub: `${adminDashboard.phaseTwo?.activeInvestigations ?? 0} active`, icon: <ReportIcon />, color: '#EC4899' },
            { title: 'Total Blood Tests', value: adminDashboard.phaseTwo?.totalBloodTests, sub: `${adminDashboard.phaseTwo?.activeBloodTests ?? 0} active`, icon: <ReportIcon />, color: '#EF4444' },
            { title: 'Insurance Policies', value: adminDashboard.phaseTwo?.totalInsurancePolicies, sub: `${adminDashboard.phaseTwo?.activeInsurancePolicies ?? 0} active`, icon: <PeopleIcon />, color: '#10B981' },
            { title: 'Total Labs', value: adminDashboard.phaseTwo?.totalLabs, sub: `${adminDashboard.phaseTwo?.activeLabs ?? 0} active`, icon: <StatsIcon />, color: '#6366F1' },
            { title: 'Pending Lab Approvals', value: adminDashboard.phaseTwo?.pendingLabApprovals, sub: `${adminDashboard.phaseTwo?.pendingInsuranceVerifications ?? 0} ins. pending`, icon: <ScheduleIcon />, color: '#B45309' },
          ].map((card, index) => (
            <Grid item xs={12} sm={6} md={2} key={`p2-${index}`}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{card.title}</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>{(card.value ?? 0).toLocaleString()}</Typography>
                      <Typography variant="caption" color="text.secondary">{card.sub}</Typography>
                    </Box>
                    <Avatar sx={{ backgroundColor: 'transparent', color: card.color }}>{card.icon}</Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Stats */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReportIcon color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Visit Reports
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ mt: 1, fontWeight: 600 }}>
                {stats.totalVisitReports?.toLocaleString() || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon color="secondary" />
                <Typography variant="body2" color="text.secondary">
                  Family Members
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ mt: 1, fontWeight: 600 }}>
                {stats.totalFamilyMembers?.toLocaleString() || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="success" />
                <Typography variant="body2" color="text.secondary">
                  Today's Appointments
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ mt: 1, fontWeight: 600 }}>
                {stats.todayAppointments?.toLocaleString() || 0}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <StatsIcon color="info" />
                <Typography variant="body2" color="text.secondary">
                  Active Doctors
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ mt: 1, fontWeight: 600 }}>
                {stats.activeDoctors?.toLocaleString() || 0} / {stats.totalDoctors?.toLocaleString() || 0}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Recent Appointments */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
              <CardHeader
                title="Recent Appointments"
                action={
                  <Tooltip title="View All">
                    <IconButton size="small">
                      <MoreIcon />
                    </IconButton>
                  </Tooltip>
                }
              />
              <CardContent>
                {recentAppointments && recentAppointments.length > 0 ? (
                  <Box>
                    {recentAppointments.slice(0, 5).map((appointment, index) => (
                      <Box
                        key={appointment.id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 2,
                          borderBottom: index < 4 ? '1px solid #eee' : 'none',
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {appointment.patientName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Dr. {appointment.doctorName} • {appointment.appointmentDate}
                          </Typography>
                        </Box>
                        <Chip
                          label={appointment.status}
                          size="small"
                          color={
                            appointment.status === 'Completed' ? 'success' :
                            appointment.status === 'Pending' ? 'warning' :
                            appointment.status === 'Cancelled' ? 'error' : 'default'
                          }
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No recent appointments
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Activities */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)', height: '100%' }}>
              <CardHeader
                title="Recent Activities"
                action={
                  <Tooltip title="Notifications">
                    <IconButton size="small">
                      <NotificationIcon />
                    </IconButton>
                  </Tooltip>
                }
              />
              <CardContent>
                {recentActivities && recentActivities.length > 0 ? (
                  <Box>
                    {recentActivities.slice(0, 6).map((activity, index) => (
                      <Box
                        key={activity.id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          py: 1.5,
                          borderBottom: index < 5 ? '1px solid #eee' : 'none',
                        }}
                      >
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'transparent' , color: '#1D4ED8', border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                          {activity.actorName?.charAt(0) || 'U'}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {activity.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {activity.timestamp}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No recent activities
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Doctor Summary */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
              <CardHeader title="Active Doctors" />
              <CardContent>
                {doctorSummary && doctorSummary.length > 0 ? (
                  <Box>
                    {doctorSummary.slice(0, 4).map((doctor, index) => (
                      <Box
                        key={doctor.id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 1.5,
                          borderBottom: index < 3 ? '1px solid #eee' : 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#15803D', border: '2px solid rgba(21, 128, 61, 0.2)' }}>
                            <DoctorIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              Dr. {doctor.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {doctor.specialization} • {doctor.hospitalName}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {doctor.todayAppointments} today
                          </Typography>
                          <Chip
                            label={doctor.status}
                            size="small"
                            color={doctor.status === 'Active' ? 'success' : 'default'}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No doctors found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Officer Summary */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
              <CardHeader title="Registered Officers" />
              <CardContent>
                {officerSummary && officerSummary.length > 0 ? (
                  <Box>
                    {officerSummary.slice(0, 4).map((officer, index) => (
                      <Box
                        key={officer.id || index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 1.5,
                          borderBottom: index < 3 ? '1px solid #eee' : 'none',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: 'transparent' , color: '#1D4ED8', border: '2px solid rgba(29, 78, 216, 0.2)' }}>
                            <PeopleIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {officer.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {officer.designation} • {officer.department}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {officer.familyMembersCount} family
                          </Typography>
                          <Chip
                            label={officer.status}
                            size="small"
                            color={officer.status === 'Active' ? 'success' : 'default'}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No officers found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
    </Box>
  );
};

export default AdminDashboardPage;